<!--
  Startseite für die Tipp-App: 2 CTA-Buttons → kompakte Tages-Spieleliste (Tag-Navigation,
  Auto-Scroll zum nächsten Spiel, Countdown nur heute/morgen, Story-Button pro Spiel).

  Nutzt dieselbe Datenabruf-Logik (useSpielplanData) wie das Spielplan-Widget - komplett
  eigenständiges Custom Element, aber geteilte Infrastruktur.
-->
<template>
  <div class="ts-app">
    <div class="ts-scroll-bereich">

      <div class="ts-spiele-bereich">
        <div class="ts-tag-nav" ref="tagNavRef">
          <div class="ts-tag-nav-1">
            <button type="button" class="ts-tag-nav__btn" @click="tagOffset--" aria-label="Vorheriger Tag">‹</button>
            <button type="button" class="ts-tag-nav__label ts-tag-nav__label--klickbar" @click="kalenderUmschalten">
              <span class="tm-day-nav__weekday">{{ tagLabel.wochentagShort }}</span>
              <span class="tm-day-nav__date">{{ tagLabel.datum }}</span>
            </button>
            <button type="button" class="ts-tag-nav__btn" @click="tagOffset++" aria-label="Nächster Tag">›</button>
          </div>
          <button type="button" class="ts-tag-nav__heute" @click="tagOffset = 0" aria-label="Zu heute springen">📍</button>
          <!-- GEÄNDERT: Button bleibt jetzt IMMER sichtbar (kein v-if mehr) - vorher komplett
               ausgeblendet, solange keine Liga-Favoriten definiert waren, was wie ein
               fehlendes Feature wirkte statt wie ein bewusster Zustand. Bei Klick ohne
               Favoriten zeigt sich stattdessen ein kurzer Hinweis-Bubble, statt stillschweigend
               nichts zu tun oder zu filtern. -->
          <div class="ts-liga-fav-wrapper">
            <button
              type="button"
              class="tm-liga-mini-toggle"
              :class="{ 'tm-liga-mini-toggle--aktiv': activeLiga === 'favoriten' }"
              :aria-label="activeLiga === 'favoriten' ? 'Alle Ligen zeigen' : 'Nur Favoriten-Ligen zeigen'"
              @click="ligaFavoritenKlick"
            >⭐</button>
            <div v-if="zeigeFavoritenHinweis" class="ts-liga-fav-hinweis">
              Noch kein Favorit festgelegt. Bitte im Spielplan Favoriten wählen.
            </div>
          </div>
          <button type="button" class="ts-tag-nav__gameplan" @click="showGameplan()">📆 Spielplan</button>
          <button type="button" class="ts-tag-nav__raster" @click="zeigeRaster = true" aria-label="Tagesraster">▦</button>
        </div>

        <div v-if="loading" class="ts-state">
          <div class="tm-state__spinner--startseite"></div>
          <p>Spiele werden geladen</p>
        </div>
        <div v-else-if="error" class="ts-state">Konnte nicht geladen werden.</div>
        <div v-else-if="tagesSpiele.length === 0" class="ts-state">Keine Spiele an diesem Tag.</div>

        <div v-else ref="listeRef" class="ts-spiele-liste" v-scroll-melden>
          <div
            v-for="eintrag in tagesSpieleMitInfo"
            :key="eintrag.match.id"
            :ref="el => registriereZeile(eintrag.match.id, el)"
            :class="['ts-spiel-zeile', `ts-spiel-zeile--${matchStatus(eintrag.match)}`]"
          >
            <div class="ts-spiel-zeit">
              <span v-if="matchStatus(eintrag.match) === 'live'" class="ts-live-dot" aria-hidden="true"></span>
              <span class="ts-spiel-zeit-wert">{{ matchStatus(eintrag.match) === 'live' ? ('Live' + (spieluhrText(eintrag.match) ? ' ' + spieluhrText(eintrag.match) : '')) : uhrzeit(eintrag.match.zeit) }}</span>
              <img v-if="eintrag.match.ligaFlaggeBild" :src="eintrag.match.ligaFlaggeBild" class="ts-spiel-flagge" alt="" />
              <span class="ts-spiel-liga">{{ eintrag.match.liga }}</span>
              <!-- GEÄNDERT: eintrag.info kommt jetzt aus dem tagesSpieleMitInfo-computed (siehe
                   Script) - wird NUR neu berechnet, wenn sich Match-Felder (Story/Lineup/etc.)
                   tatsächlich ändern, NICHT bei jedem Tick der Uhr (die für den Countdown
                   nebenan gebraucht wird, aber mit der Detail-Info nichts zu tun hat). Vorher
                   lief ermittleDetailInfo() als reiner Funktionsaufruf im Template mit, der bei
                   JEDEM Re-Render (auch nur wegen der Uhr) unnötig neu ausgeführt wurde. -->
              <button
                type="button"
                :class="['tm-karte-btn', { 'tm-karte-btn--story': eintrag.match.storyPreview || eintrag.match.storyReport }]"
                :aria-label="(eintrag.match.storyPreview || eintrag.match.storyReport) ? 'Story und Details öffnen' : 'Details öffnen'"
                @click.stop="oeffneDetails(eintrag.match)"
              >
                <svg v-if="eintrag.match.storyPreview || eintrag.match.storyReport" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M4 5h13a2 2 0 0 1 2 2v12a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2V5z"/><path d="M19 9h2v9a2 2 0 0 1-2 2"/><path d="M8 9h7M8 13h7M8 17h4"/>
                </svg>
                <span>{{ (eintrag.match.storyPreview || eintrag.match.storyReport) ? 'Story' : 'Details' }}</span>
                <svg v-if="!(eintrag.match.storyPreview || eintrag.match.storyReport)" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
              </button>
            </div>
            <div class="ts-spiel-teams">
              <span class="ts-spiel-team ts-spiel-team--heim">
                <span class="ts-spiel-teamname">{{ eintrag.match.heim }}</span>
                <span class="ts-spiel-teamlogo"><img v-if="eintrag.match.heimLogo" :src="eintrag.match.heimLogo" class="ts-spiel-logo" alt="" /></span>
              </span>
              <span :class="['ts-spiel-mitte', { 'ts-spiel-mitte--beendet': matchStatus(eintrag.match) === 'beendet', 'ts-spiel-mitte--countdown': matchStatus(eintrag.match) !== 'beendet' && matchStatus(eintrag.match) !== 'live' }]">
                <template v-if="matchStatus(eintrag.match) === 'beendet' || matchStatus(eintrag.match) === 'live'">
                  {{ eintrag.match.heimtore ?? '-' }}:{{ eintrag.match.austore ?? '-' }}
                </template>
                <!-- Countdown nur für heute/morgen (tagOffset 0/1) - an entfernteren Tagen bringt
                     ein tickender Countdown nichts, da reicht die reine Uhrzeit oben. -->
                <template v-else-if="tagOffset === 0 || tagOffset === 1">{{ formatCountdown(eintrag.match.zeit, jetzt) }}</template>
              </span>
              <span class="ts-spiel-team ts-spiel-team--auswaerts">
                <span class="ts-spiel-teamlogo"><img v-if="eintrag.match.auswaertsLogo" :src="eintrag.match.auswaertsLogo" class="ts-spiel-logo" alt="" /></span>
                <span class="ts-spiel-teamname">{{ eintrag.match.auswaerts }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Teleport :to="portalZiel">
      <SpielplanRasterModal
        v-if="zeigeRaster"
        :matches="tagesSpiele"
        :label="tagLabel.text"
        :ziel="portalZiel"
        @close="zeigeRaster = false"
        @select="m => { oeffneDetails(m) }"
        @prev="tagVerschieben(-1)" 
        @next="tagVerschieben(1)"
      />
      <Transition name="tm-sheet-fade">
        <div v-if="offenesDetail" class="tm-sheet-backdrop" @click="offenesDetail = null"></div>
      </Transition>
      <Transition name="tm-sheet-slide">
        <DetailOverlay
          v-if="offenesDetail"
          :detail="offenesDetail"
          :tabellen="tabellen"
          :api-base="props.apiBase"
          @close="offenesDetail = null"
          @change-tab="wechsleDetailTab"
        />
      </Transition>
      <Transition name="tm-kalender-auf">
        <KalenderPopover
          v-if="zeigeKalender"
          class="tm-kalender-popover tm-kalender-popover--teleportiert"
          :style="kalenderPositionStyle"
          :verfuegbare-tage="verfuegbareTage"
          :spieltage-gruppen="spieltageGruppen"
          :ausgewaehlter-tag="ausgewaehlterTagSchluessel"
          @select-day="tagAusKalenderWaehlen"
        />
      </Transition>
      <div v-if="zeigeKalender" ref="kalenderElement" class="tm-kalender-backdrop" @click="zeigeKalender = false"></div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useSpielplanData } from './composables/useSpielplanData.js'
import { useLigaFavoriten } from './composables/useLigaFavoriten.js'
import { useLiveScores } from './composables/useLiveScores.js'
import { useClock } from './composables/useClock.js'
import { tagSchluessel, formatTagAnzeige, formatTag, formatTagZweiteilig, formatUhrzeit, formatCountdown, ermittleAnzeigeStatus, ermittleWsUrl } from './lib/format.js'
import { getPortalTarget } from './lib/portal.js'
import { vScrollMelden } from './lib/scrollLock.js'
import DetailOverlay from './components/DetailOverlay.vue'
import KalenderPopover from './components/KalenderPopover.vue'
import { useKalenderPopover } from './composables/useKalenderPopover.js'
import SpielplanRasterModal from './components/SpielplanRasterModal.vue'

const props = defineProps({
  // Datenabruf - dieselben Attribute wie beim Spielplan-Widget
  companyId: { type: Number, required: true },
  // Optional: expliziter Cache-Schlüssel, der die automatische Herleitung aus den anderen Props
  // überstimmt - z.B. cache-key="startseite-hauptseite" auf BEIDEN Widgets gesetzt garantiert
  // denselben geteilten Fetch, unabhängig von sonstigen Attribut-Abweichungen.
  cacheKey: { type: String, default: null },
  // von/bis: identisch zum Spielplan-Widget deklariert, damit beide bei gleichem Wert denselben
  // Cache-Key treffen (und sich so den REST-Fetch teilen) - fehlte hier bisher komplett, wodurch
  // die Startseite immer mit von=null/bis=null rechnete und dadurch einen ZWEITEN, separaten
  // Fetch auslöste, selbst wenn das Spielplan-Widget von/bis explizit gesetzt hatte.
  von: { type: String, default: null },
  bis: { type: String, default: null },
  apiBase: { type: String, default: '/rest/courseservice/oddsservice/free' },
  // Basis-URL für relative Bild-Pfade in Story-Daten (imageUrl/avatarUrl) und Länderflaggen -
  // Default die Produktions-Domain, lokal beim Testen z.B. auf "" setzen (siehe
  // useSpielplanData.js: bildBasisUrl).
  bildBasisUrl: { type: String, default: 'https://app.team-match.io' },
  wsUrl: { type: String, default: '/ws-result-service' },
  scope: { type: String, default: 'global' },
  veranstalterId: { type: String, default: '' },
  priorityLeagues: { type: [Array, String], default: () => [] },

  // Ab wie vielen Minuten vor Anpfiff der Countdown clientseitig gezeigt wird (identisch zum
  // Spielplan-Widget) - war bisher nicht deklariert, obwohl das Attribut schon gesetzt wurde.
  countdownAbMinuten: { type: Number, default: 120 }
})

const portalZiel = getPortalTarget()

const { matches, loading, error, tabellen, activeLiga, loadMatches, aufraeumen, ladeStoriesFuerSichtbare, verfuegbareTage, spieltageGruppen } = useSpielplanData(props, null)
const { istLigaFavorit, ligaFavoriten } = useLigaFavoriten()

const { zeigeKalender, tagNavRef, kalenderPositionStyle, kalenderUmschalten, kalenderSchliessen } = useKalenderPopover()

const zeigeRaster = ref(false)
const zeigeFavoritenHinweis = ref(false)
let favoritenHinweisTimeout = null

const { 
  verbinden: wsVerbinden, aufraeumen: wsAufraeumen, 
  wiederholeGepuffertes,
  onReaktiviert 
} = useLiveScores(ermittleWsUrl(props.wsUrl), matches)
const { jetzt } = useClock()
const stopReaktiviert = onReaktiviert(loadMatches)

function ligaFavoritenKlick() {
  if (ligaFavoriten.value.size === 0) {
    zeigeFavoritenHinweis.value = true
    clearTimeout(favoritenHinweisTimeout)
    favoritenHinweisTimeout = setTimeout(() => { zeigeFavoritenHinweis.value = false }, 3500)
    return
  }
  activeLiga.value = activeLiga.value === 'favoriten' ? 'alle' : 'favoriten'
}

// Kalender-Auswahl auf das bestehende tagOffset-System der Startseite ummünzen - die Startseite
// nutzt (anders als SpielplanWidget) keinen eigenen ausgewaehlterTag-Ref aus der Composable,
// sondern zählt relativ zu heute (tagOffset). Differenz in Tagen zwischen gewähltem Kalender-Tag
// und heute ergibt den passenden Offset.
function tagAusKalenderWaehlen(schluessel) {
  const heute = new Date()
  heute.setHours(0, 0, 0, 0)
  const ziel = new Date(schluessel)
  ziel.setHours(0, 0, 0, 0)
  tagOffset.value = Math.round((ziel - heute) / 86400000)
  kalenderSchliessen()
}

function tagVerschieben(delta) {
  tagOffset.value += delta
}

onMounted(async () => {
  await loadMatches() // erst Daten laden ...
  wsVerbinden() // ... dann verbinden (geteilt über alle Instanzen mit derselben wsUrl)
  wiederholeGepuffertes() // falls schon vor diesem Moment WS-Nachrichten für andere Instanzen
  // angekommen sind (z.B. weil das Spielplan-Widget die Verbindung zuerst geöffnet hat), hier
  // sofort den Zwischenstand nachholen, statt auf die nächste natürliche Nachricht zu warten
})
onUnmounted(() => {
  aufraeumen()
  wsAufraeumen()
  stopReaktiviert()
  clearTimeout(favoritenHinweisTimeout)
})

function uhrzeit(zeit) {
  return formatUhrzeit(zeit)
}

// Reaktiv statt rohem match.status - kippt automatisch auf 'live', sobald die Anstoßzeit
// erreicht wird, auch ohne WebSocket-Update oder Neuladen der Seite (siehe format.js).
function matchStatus(match) {
  return ermittleAnzeigeStatus(match, jetzt.value, props.countdownAbMinuten)
}

// "HT" bei Halbzeit, sonst Minute inkl. Nachspielzeit als "45+6'" - identisch zur Logik im
// Spielplan-Widget (MatchCard.vue).
function spieluhrText(match) {
  if (match.spielUhrKurz === 'HT') return 'HT'
  if (typeof match.minute !== 'number' || match.minute < 0) return ''
  const extra = match.spielUhrExtra
  const extraSuffix = typeof extra === 'number' && extra > 0 ? `+${extra}` : ''
  return `${match.minute}${extraSuffix}'`
}

// TODO: Sobald das Backend ein echtes "tippbar"-Feld liefert (Info kommt noch), hier auf
// match.tippbar (o.ä. echten Feldnamen) umstellen. Bis dahin als sinnvoller Platzhalter:
// beendete Spiele sind nie tippbar, alles andere schon.
function istTippbar(match) {
  if (typeof match.tippbar === 'boolean') return match.tippbar
  return match.status !== 'beendet'
}

// ---------- EIN Button pro Zeile (oben rechts) - öffnet was auch immer verfügbar ist ----------
// Sichtbar, sobald IRGENDETWAS für das Match zu zeigen ist (Story, Aufstellung, Events oder
// Statistik), öffnet automatisch den sinnvollsten verfügbaren Tab. Zusätzlich optisch deutlicher
// hervorgehoben (siehe hatStory/CSS), wenn eine Story vorhanden ist - Story ist der wertvollste
// Inhalt, soll also auch am meisten auffallen, statt gleich auszusehen wie "nur Statistik da".
const offenesDetail = ref(null) // { match, tab } - wiederverwendet DetailOverlay 1:1

// GEÄNDERT: bestimmeTab/detailIcon/detailLabel/hatDetails/hatStory waren fünf getrennte
// Funktionen, die sich teils gegenseitig erneut aufgerufen haben (detailLabel rief bestimmeTab
// nochmal auf, das Template rief detailIcon UND detailLabel UND hatDetails UND hatStory JEWEILS
// einzeln auf - macht bestimmeTab() pro Match-Zeile bis zu 3x). Jetzt EINE Funktion, die alles
// in einem Durchlauf berechnet - wird im Template nur noch EINMAL pro Match aufgerufen (siehe
// v-for="info in [ermittleDetailInfo(match)]"-Trick weiter unten).
function ermittleDetailInfo(match) {
  // GEÄNDERT: hatLineup/hatEvents/hatStatistik verlassen sich bisher AUSSCHLIESSLICH auf die
  // vom Server separat gelieferten Flags - die können aber falsch/veraltet sein (beobachtet:
  // events-Array hatte 11 Einträge, hatEvents trotzdem false). Jetzt zusätzlich die
  // TATSÄCHLICHEN Arrays prüfen (z.B. nach einem Lazy-Load, der die Flags nicht mit
  // aktualisiert) - robuster gegen genau diese Backend-Inkonsistenz.
  const hatLineup = !!match.hatLineup || (Array.isArray(match.lineups) && match.lineups.length > 0)
  const hatEvents = !!match.hatEvents || (Array.isArray(match.events) && match.events.length > 0)
  const hatStatistik = !!match.hatStatistik || (Array.isArray(match.statistiken) && match.statistiken.length > 0)

  const spielberichtVerfuegbar = match.status === 'beendet' && !!match.storyReport
  const tab = spielberichtVerfuegbar ? 'spielbericht'
    : match.storyPreview ? 'vorschau'
    : hatLineup ? 'aufstellung'
    : hatEvents ? 'events'
    : hatStatistik ? 'statistik'
    : match.ligaId != null ? 'tabelle' // Tabelle als Fallback - jede Liga mit bekannter ID hat potenziell eine
    : 'vorschau' // Fallback, kommt praktisch nie vor (hatDetails wäre dann false)

  return {
    tab,
    label: { vorschau: 'Vorschau', spielbericht: 'Spielbericht', aufstellung: 'Aufstellung', events: 'Events', statistik: 'Statistik', tabelle: 'Tabelle' }[tab],
    icon: spielberichtVerfuegbar ? '📝' : (match.storyPreview ? '📰' : 'ℹ️'),
    hatStory: !!(match.storyPreview || match.storyReport),
    hatDetails: !!(match.storyPreview || match.storyReport || hatLineup || hatEvents || hatStatistik || match.ligaId != null)
  }
}

// NEU: Berechnet ermittleDetailInfo() EINMAL pro Match und cached das Ergebnis über Vues
// computed-Mechanismus - wird NUR neu ausgewertet, wenn sich tagesSpiele oder die jeweiligen
// Match-Felder (Story/Lineup/etc.) tatsächlich ändern. Vorher lief ermittleDetailInfo() als
// reiner Funktionsaufruf im Template mit, der bei JEDEM Component-Re-Render (auch nur wegen der
// jede Sekunde tickenden Uhr für den Countdown nebenan) unnötig erneut ausgeführt wurde, obwohl
// sich an den Detail-relevanten Feldern nichts geändert hatte.
const tagesSpieleMitInfo = computed(() =>
  tagesSpiele.value.map(match => ({ match, info: ermittleDetailInfo(match) }))
)
// NEU: unterscheidet, ob der aktuell gezeigte Tab automatisch (Fallback) oder vom Nutzer
// BEWUSST gewählt wurde - der Korrektur-Watcher unten darf nur im ersten Fall eingreifen, sonst
// würde er einen bewussten Klick auf "Tabelle" sofort wieder wegkorrigieren.
let tabWurdeAutomatischGewaehlt = true

// GEÄNDERT: async - wartet jetzt zuerst auf ein eventuell noch laufendes Story-Nachladen,
// BEVOR der Tab bestimmt und das Overlay geöffnet wird. Das ist die eigentliche Lösung fürs
// "kurz Tabelle sichtbar, dann Wechsel"-Aufblitzen - der reaktive Korrektur-Watcher weiter unten
// lief zwar korrekt, aber IMMER erst NACH dem (sichtbar falschen) ersten Render. Jetzt öffnet
// sich das Overlay verzögert, dafür gleich mit dem richtigen Tab, kein Flackern mehr.
async function oeffneDetails(match) {
  tabWurdeAutomatischGewaehlt = true
  if (ladeStoriesPromise) {
    try {
      await ladeStoriesPromise
    } catch {
      // Fehler beim Nachladen ignorieren - wir öffnen trotzdem mit dem, was wir haben
      // (ermittleDetailInfo liefert dann eben den Tabelle-Fallback, kein Absturz)
    }
  }
  offenesDetail.value = { match, tab: ermittleDetailInfo(match).tab }
}

function wechsleDetailTab(tab) {
  tabWurdeAutomatischGewaehlt = false // Nutzer hat selbst gewählt - ab jetzt nicht mehr automatisch korrigieren
  if (offenesDetail.value) offenesDetail.value = { ...offenesDetail.value, tab }
}

// Korrigiert den Tab nur noch, solange tabWurdeAutomatischGewaehlt true ist (siehe oben) -
// verhindert, dass ein bewusster manueller Tab-Wechsel sofort wieder überschrieben wird.
watch(() => offenesDetail.value ? [
  offenesDetail.value.match.storyPreview,
  offenesDetail.value.match.storyReport,
  offenesDetail.value.match.hatLineup,
  offenesDetail.value.match.hatEvents,
  offenesDetail.value.match.hatStatistik
] : null, () => {
  if (!offenesDetail.value || !tabWurdeAutomatischGewaehlt) return
  const bessererTab = ermittleDetailInfo(offenesDetail.value.match).tab
  if (bessererTab !== offenesDetail.value.tab) {
    offenesDetail.value = { ...offenesDetail.value, tab: bessererTab }
  }
})

// ---------- Tag-Navigation: 0 = heute, +1/-1 = ein Tag vor/zurück ----------
const tagOffset = ref(0)

const ausgewaehlterTagSchluessel = computed(() => {
  const d = new Date()
  d.setDate(d.getDate() + tagOffset.value)
  return tagSchluessel(d)
})

const tagLabel = computed(() => formatTagAnzeige(ausgewaehlterTagSchluessel.value))

const tagesSpiele = computed(() =>
  matches.value
    .filter(m => tagSchluessel(m.zeit) === ausgewaehlterTagSchluessel.value)
    // Gleicher Liga-Filter wie im Spielplan-Widget (vorgefilterteMatches in useSpielplanData.js)
    // - hier bisher gefehlt, weshalb die Startseite trotz aktivem "Favoriten"-Modus immer ALLE
    // Ligen gezeigt hat. Startseite hat zwar kein eigenes Liga-Dropdown, aber activeLiga startet
    // ja automatisch auf 'favoriten', falls welche gesetzt sind - das muss hier genauso greifen.
    .filter(m => activeLiga.value === 'alle'
      || (activeLiga.value === 'favoriten' ? istLigaFavorit(m.ligaKey) : m.ligaKey === activeLiga.value))
    .sort((a, b) => new Date(a.zeit) - new Date(b.zeit))
)

// NEU: Promise des laufenden Story-Nachladens merken - oeffneDetails() wartet darauf, BEVOR
// es den Tab bestimmt und das Overlay öffnet (siehe unten). Verhindert das sichtbare
// "kurz Tabelle, dann Wechsel"-Aufblitzen, das der reaktive Korrektur-Watcher allein verursachte.
let ladeStoriesPromise = null

watch(tagesSpiele, (sichtbar) => {
  ladeStoriesPromise = ladeStoriesFuerSichtbare(sichtbar)
}, { immediate: true })

// ---------- Auto-Scroll zum nächsten offenen/laufenden Spiel ----------
const listeRef = ref(null)
const zeilenElemente = new Map() // match.id -> DOM-Element, für den Scroll-Zielpunkt

function registriereZeile(matchId, el) {
  if (el) zeilenElemente.set(matchId, el)
  else zeilenElemente.delete(matchId)
}

function scrolleZumNaechstenSpiel() {
  // "Nächstes" Spiel = erstes noch nicht beendetes (live zählt auch als "aktuell relevant")
  const ziel = tagesSpiele.value.find(m => m.status !== 'beendet')
  if (!ziel) return
  const el = zeilenElemente.get(ziel.id)
  if (el && listeRef.value) {
    el.scrollIntoView({ block: 'start' })
  }
}

function showGameplan() {
    const tabsContent = document.querySelector(".tabs-content");
    if (!tabsContent) return;
    const standingsForm = tabsContent.querySelector(".form-my-standings");
    if (!standingsForm) return;

    // Alle GERADE aktiven Panes deaktivieren (nicht nur ".form-start-overview" fix annehmen) -
    // falls der Nutzer von einem anderen Tab aus klickt, würde die alte Version das übersehen
    // und beide Panes blieben gleichzeitig "active" stehen.
    tabsContent.querySelectorAll(".active").forEach(pane => {
        pane.classList.remove("in", "active");
    });

    // "fade" absichtlich nicht anfassen - das steuert nur, OB ein Pane überhaupt animiert ist,
    // und sollte laut Bootstrap-Konvention dauerhaft am Element bleiben, nicht bei jedem Klick
    // hin- und hergeschoben werden (sonst bricht die Übergangs-Animation).
    standingsForm.classList.add("in", "active");
}


// Merkt sich, für welchen Tag zuletzt gescrollt wurde - verhindert, dass JEDES Live-Update
// (das tagesSpiele wegen der neuen Array-Referenz aus useLiveScores triggert) erneut zum
// nächsten Spiel scrollt. Nur beim initialen Laden ODER echtem Tag-Wechsel (tagOffset) soll
// gescrollt werden, nicht bei jedem WS-Tick innerhalb desselben Tages.
let letzterGescrollterTagSchluessel = null

watch(tagesSpiele, (spiele) => {
  if (spiele.length === 0) return
  if (letzterGescrollterTagSchluessel === ausgewaehlterTagSchluessel.value) return
  letzterGescrollterTagSchluessel = ausgewaehlterTagSchluessel.value
  nextTick(() => scrolleZumNaechstenSpiel())
})
</script>